import {
  AccountType,
  accountTypeToRoutePrefix,
  parseAccountType,
} from '../../authentication/models/account-type.enum';

/** Reserved first path segments — not treated as tenant slugs. */
export const RESERVED_ROUTE_SEGMENTS = new Set([
  'auth',
  'admin',
  'maker',
  'checker',
  'dashboard',
  'assets',
]);

/** Default dashboard segment per role (each loads a different component). */
const ROLE_DASHBOARD_SEGMENT = 'dashboard';

/**
 * Home URL after login for a role (`/{admin|maker|checker}/dashboard`).
 * Tenant is resolved from subdomain, not repeated in the path.
 */
export function roleHomePath(accountType: AccountType): string {
  const prefix = accountTypeToRoutePrefix(accountType);
  return `/${prefix}/${ROLE_DASHBOARD_SEGMENT}`;
}

export function resolveAccountTypeFromUser(
  accountType: unknown,
  role?: unknown,
): AccountType | null {
  const parsed = parseAccountType(accountType);
  if (parsed) {
    return parsed;
  }
  if (typeof role === 'string') {
    return parseAccountType(role);
  }
  return null;
}

/**
 * Whether a URL belongs to the given role's route tree (blocks cross-role returnUrl).
 * Accepts legacy tenant-prefixed URLs for backward-compatible returnUrl validation.
 */
export function isUrlAllowedForAccountType(
  url: string,
  accountType: AccountType,
  tenantSlug?: string | null,
): boolean {
  const path = url.split('?')[0];
  const prefix = accountTypeToRoutePrefix(accountType);
  const roots = [`/${prefix}`];

  if (tenantSlug) {
    roots.push(`/${tenantSlug}/${prefix}`);
  }

  return roots.some(
    (root) => path === root || path.startsWith(`${root}/`),
  );
}

/**
 * Builds a role-scoped path (`/admin/audit-trail`, `/maker/dashboard`, etc.).
 */
export function rolePath(path: string, accountType: AccountType): string {
  const prefix = accountTypeToRoutePrefix(accountType);
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `/${prefix}${normalized}`;
}

/** Normalizes an in-app path (tenant lives on subdomain, not in the URL). */
export function appPath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}
